import { THINKING_WARN_THRESHOLD_MS } from '@orn/global/assistant/timing.js';
import formatBytes from '@orn/shared-web/utils/formatBytes';

import { formatTokens } from './promptBudgetView';
import { formatCostUsd, formatDurationMs } from './transcript/transcriptModel';

import type {
  IMopsAssistantBenchmarkIterationTranscriptV1,
  IMopsAssistantBenchmarkRoundTranscriptV1,
  IMopsAssistantBenchmarkScenarioTranscriptsV1,
} from '@orn/global/assistant/MopsAssistantBenchmarkArtifact.js';

/**
 * Pure view helpers for the base-vs-head transcript modal. Every branch a
 * component would otherwise carry — null usage/latency, redacted thinking,
 * truncated tool results, pass/fail run ordering, cost roll-ups — resolves
 * here, so the JSX stays a thin map and the coverage lives in one unit test.
 * The module is deliberately free of JSX and Tailwind: it returns semantic
 * `tone` enums (like `promptBudgetView`), and the renderer maps them to theme
 * classes.
 */

/** Which side of the comparison a captured run came from. */
export type TranscriptArm = 'base' | 'head';

/** Displayed for a redacted thinking block — never the opaque provider bytes. */
export const REDACTED_THINKING_PLACEHOLDER = '🔒 redacted reasoning';

/** Rolled-up totals for one captured run, for the left rail's at-a-glance line. */
export interface IIterationRollup {
  /** Summed cost across rounds that reported usage (USD). */
  costUsd: number;
  /** Summed generated (output) tokens across rounds that reported usage. */
  outputTokens: number;
  /** Summed model latency across rounds that reported it (ms). */
  latencyMs: number;
  /** Total rounds across every turn. */
  roundCount: number;
  /** True when some round lacked usage or latency, so the sums are a lower bound. */
  partial: boolean;
  /** Preformatted display strings; a metric no round measured renders as `—`. */
  display: { cost: string; outputTokens: string; latency: string };
}

/**
 * Sum a run's cost/tokens/latency across all of its turns' rounds. Rounds
 * missing `usage` (or `latencyMs`) are skipped rather than counted as zero, and
 * flip `partial` so the rail can mark the totals as a floor; a metric that no
 * round reported renders `—` instead of a misleading `0`.
 */
export function iterationRollup(iteration: IMopsAssistantBenchmarkIterationTranscriptV1): IIterationRollup {
  let costUsd = 0;
  let outputTokens = 0;
  let latencyMs = 0;
  let roundCount = 0;
  let usageRounds = 0;
  let latencyRounds = 0;

  for (const turn of iteration.turns) {
    for (const round of turn.rounds) {
      roundCount += 1;
      if (round.usage !== null) {
        costUsd += round.usage.costUsd;
        outputTokens += round.usage.outputTokens;
        usageRounds += 1;
      }
      if (round.latencyMs !== null) {
        latencyMs += round.latencyMs;
        latencyRounds += 1;
      }
    }
  }

  return {
    costUsd,
    outputTokens,
    latencyMs,
    roundCount,
    partial: usageRounds < roundCount || latencyRounds < roundCount,
    display: {
      cost: usageRounds > 0 ? formatCostUsd(costUsd) : '—',
      outputTokens: usageRounds > 0 ? formatTokens(outputTokens) : '—',
      latency: latencyRounds > 0 ? formatDurationMs(latencyMs) : '—',
    },
  };
}

/** One selectable run in the modal's left rail. */
export interface IRunRef {
  arm: TranscriptArm;
  iterationIndex: number;
  passed: boolean;
  rollup: IIterationRollup;
}

/** One arm's group of selectable runs. `runs` is empty when the arm wasn't captured. */
export interface IArmRuns {
  arm: TranscriptArm;
  runs: IRunRef[];
}

const ARM_ORDER: readonly TranscriptArm[] = ['base', 'head'];

/**
 * Build the left-rail model: a group per arm (base first, then head), each run
 * tagged pass/fail with its roll-up. Runs are ordered pass-before-fail, then by
 * iteration index, so the representative pass and fail always land in the same
 * spots regardless of the producer's array order. A `null` (uncaptured) arm
 * yields an empty `runs` list rather than being dropped, so the rail can still
 * name the missing side.
 */
export function buildRunList(transcripts: IMopsAssistantBenchmarkScenarioTranscriptsV1): IArmRuns[] {
  return ARM_ORDER.map((arm) => {
    const iterations = transcripts[arm] ?? [];
    const runs: IRunRef[] = iterations
      .map((it) => ({ arm, iterationIndex: it.iterationIndex, passed: it.passed, rollup: iterationRollup(it) }))
      .sort((a, b) => (a.passed === b.passed ? a.iterationIndex - b.iterationIndex : a.passed ? -1 : 1));
    return { arm, runs };
  });
}

/** Semantic color role for a per-round stat chip; the renderer maps it to a theme token. */
export type RoundStatTone = 'money' | 'muted' | 'neutral' | 'accent' | 'latency' | 'thinking' | 'warn' | 'none';

/** One fixed-width chip in a round's stat line. */
export interface IRoundStat {
  /** Stable identity for keying + column alignment. */
  key: 'cost' | 'cached' | 'total' | 'out' | 'lat' | 'think';
  /** Short column label. */
  label: string;
  /** Formatted value, or `—` when not measured. */
  value: string;
  tone: RoundStatTone;
}

function thinkTone(ms: number | null): RoundStatTone {
  if (ms === null) {
    return 'none';
  }
  if (ms === 0) {
    return 'muted';
  }
  return ms > THINKING_WARN_THRESHOLD_MS ? 'warn' : 'thinking';
}

/**
 * The per-round stat line: a fixed six-chip set in a stable order so the columns
 * align down the transcript. Cost/token chips key off `usage` (all `—` when the
 * provider message carried none); `total` is every token the round touched
 * (uncached input + cache read/write + output). Latency and thinking come off
 * the round timing, with thinking escalating to a `warn` tone past the shared
 * slow-thinking bar so a pathological round is visible at a glance.
 */
export function roundStats(round: IMopsAssistantBenchmarkRoundTranscriptV1): IRoundStat[] {
  const u = round.usage;
  const totalTokens = u === null ? null : u.inputTokens + u.cacheReadTokens + u.cacheWriteTokens + u.outputTokens;
  return [
    {
      key: 'cost',
      label: '$',
      value: u === null ? '—' : formatCostUsd(u.costUsd),
      tone: u === null ? 'none' : 'money',
    },
    {
      key: 'cached',
      label: 'cache',
      value: u === null ? '—' : formatTokens(u.cacheReadTokens),
      tone: u === null ? 'none' : 'muted',
    },
    {
      key: 'total',
      label: 'tot',
      value: totalTokens === null ? '—' : formatTokens(totalTokens),
      tone: totalTokens === null ? 'none' : 'neutral',
    },
    {
      key: 'out',
      label: 'out',
      value: u === null ? '—' : formatTokens(u.outputTokens),
      tone: u === null ? 'none' : 'accent',
    },
    {
      key: 'lat',
      label: 'lat',
      value: formatDurationMs(round.latencyMs),
      tone: round.latencyMs === null ? 'none' : 'latency',
    },
    { key: 'think', label: 'think', value: formatDurationMs(round.thinkingMs), tone: thinkTone(round.thinkingMs) },
  ];
}

/**
 * The text to show for a thinking block: its reasoning, or the redacted
 * placeholder when the provider returned an encrypted chain. Guarantees the
 * opaque signature bytes are never rendered as if they were readable reasoning.
 */
export function thinkingDisplayText(text: string, redacted: boolean): string {
  return redacted ? REDACTED_THINKING_PLACEHOLDER : text;
}

/** A tool result's size line, with a `truncated` marker when it was capped for display. */
export function toolResultMeta(resultBytes: number, truncated: boolean): string {
  const size = formatBytes(resultBytes);
  return truncated ? `${size} · truncated` : size;
}
